
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import {date, QBadge, QBtn, QSelect} from "quasar"
import Axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from "axios"
import _ from "lodash"
import SelectDropDown from "@/components/SelectDropDown.vue"
import RangeNumberFilter from "@/components/RangeNumberFilter.vue"
import DateRangeFilterGroup from "@/components/DateRangeFilterGroup.vue"
import Message from "@/components/Message.vue"
import AddDeleteButton from "@/components/AddDeleteButton.vue"
import {Inject} from "vue-property-decorator"
import {AuthenticationService} from "@/AuthenticationService"
import {PlanService} from "@/superannuation/PlanService"
import MemberDetailDialog from "@/superannuation/MemberDetailDialog.vue"
import MemberDocumentsDialog from "@/superannuation/MemberDocumentsDialog.vue"
import {Document} from "@/superannuation/memberTypes"
import {
  ACCOUNT_STATUS_OPTIONS,
  BENEFICIARY_NOMINATION_OPTIONS,
  CLASSIFICATION_OPTIONS,
  CONTRIBUTION_STATUS_OPTIONS,
  optionLabel,
} from "@/superannuation/memberLookups"

interface IdAndName {
  id: string
  name: string
}
interface RangeFilter {
  from: string
  to: string
}
interface DateDropdownGroup {
  id: string
  keys: Array<string>
}

// each entry renders as a single dropdown + From/To row; the dropdown lets the
// user pick which one of the listed date filters that row currently applies to.
// all of these live in the collapsible "Activity" section
const DATE_DROPDOWN_GROUPS: Array<DateDropdownGroup> = [
  {id: "createdUpdated", keys: ["created", "updated"]},
  {id: "loginActivity", keys: ["firstLogin", "lastLogin", "contactRequested"]},
  {id: "downloads", keys: ["reportDownloaded", "adviceDownloaded"]},
  {
    id: "assessments",
    keys: [
      "projectionModelled",
      "riskProfileAssessed",
      "insuranceAssessed",
      "contributionCalculated",
      "ttrCalculated",
      "beneficiaryAssessed",
    ],
  },
]

const DATE_DROPDOWN_ROWS: Array<Array<string>> = [
  ["createdUpdated", "loginActivity"],
  ["downloads", "assessments"],
]

const AGE_MIN = 15
const AGE_MAX = 75
const M100 = 100000000
const M10 = 10000000
const M1 = 1000000

// this platform's users can also filter by its paired platform
// (see MemberSummaryRepository's untaxed/taxed balance filtering)
const UNTAXED_BALANCE_PLATFORM_ID = "64a8d8b084c64b268ca1668f36a91576"
const TAXED_BALANCE_PLATFORM_ID = "74c6bc323afb4bc1a8f96a9829a5ad59"
const LIMITED_PLATFORM_IDS = [UNTAXED_BALANCE_PLATFORM_ID, TAXED_BALANCE_PLATFORM_ID]

const defaultRange = (): RangeFilter => ({from: "", to: ""})
const toRangeFilterMap = (
  acc: Record<string, RangeFilter>,
  key: string
): Record<string, RangeFilter> => {
  acc[key] = defaultRange()
  return acc
}
const toDropdownSelectionMap = (
  acc: Record<string, string>,
  g: DateDropdownGroup
): Record<string, string> => {
  acc[g.id] = g.keys[0]
  return acc
}

const DATE_FORMAT = "DD/MM/YYYY"

interface Flags {
  enabled: boolean
  activated: boolean
  locked: boolean
  expired: boolean
  sample: boolean
  superscore: boolean
  outdated: boolean
  projections: boolean
  salary: boolean
  shadow: boolean
}
interface Member {
  id: string
  number: string
  username: string
  name: string
  dob: Date
  company: IdAndName
  accountStatus: string | null
  updated: Date
  lastLogin: Date | null
  flags: Flags
  hasEvents: boolean
  docs: Array<Document>
}

const format = (ds: string) => date.formatDate(ds, DATE_FORMAT)
@Component({
  components: {
    QBtn,
    AddDeleteButton,
    RangeNumberFilter,
    DateRangeFilterGroup,
    Message,
    PageHeader,
    HelpIcon,
    QSelect,
    QBadge,
    SelectDropDown,
    MemberDetailDialog,
    MemberDocumentsDialog,
  },
})
export default class MembersView extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  @Inject()
  planService: PlanService

  readonly accountStatusOptions = ACCOUNT_STATUS_OPTIONS
  readonly classificationOptions = CLASSIFICATION_OPTIONS
  readonly contributionStatusOptions = CONTRIBUTION_STATUS_OPTIONS
  readonly beneficiaryNominationOptions = BENEFICIARY_NOMINATION_OPTIONS

  // exposed for the range-number-filter bounds in the template (module-scope
  // constants aren't visible there)
  readonly ageLowerBound = AGE_MIN
  readonly ageUpperBound = AGE_MAX
  readonly balanceUpperBound = M100 - 1
  readonly capUpperBound = M10 - 1
  readonly contributionsUpperBound = M1 - 1

  showFilters = false
  showGroup2 = true
  accountStatus: string | null = null
  ageMin: number | null = null
  ageMax: number | null = null
  classification: string | null = null
  contributionStatus: string | null = null
  beneficiaryNomination: string | null = null
  superBalanceMin: number | null = null
  superBalanceMax: number | null = null
  grossSalaryMin: number | null = null
  grossSalaryMax: number | null = null
  voluntaryContributionsMin: number | null = null
  voluntaryContributionsMax: number | null = null
  ttrBalanceMin: number | null = null
  ttrBalanceMax: number | null = null
  deathCoverMin: number | null = null
  deathCoverMax: number | null = null
  tpdCoverMin: number | null = null
  tpdCoverMax: number | null = null
  salaryContinuationCoverMin: number | null = null
  salaryContinuationCoverMax: number | null = null

  ranges: Record<string, RangeFilter> = DATE_DROPDOWN_GROUPS.flatMap(g => g.keys).reduce<
    Record<string, RangeFilter>
  >(toRangeFilterMap, {})

  dropdownSelections: Record<string, string> = DATE_DROPDOWN_GROUPS.reduce<Record<string, string>>(
    toDropdownSelectionMap,
    {}
  )

  get dateDropdownRows(): Array<Array<DateDropdownGroup>> {
    return DATE_DROPDOWN_ROWS.map(rowIds =>
      rowIds.map(id => DATE_DROPDOWN_GROUPS.find(g => g.id === id)!)
    )
  }

  private toIso(ddmmyyyy: string): string | null {
    if (_.isEmpty(ddmmyyyy)) return null
    const extracted = date.extractDate(ddmmyyyy, DATE_FORMAT)
    return date.formatDate(extracted, "YYYY-MM-DD")
  }

  // sorting is server-side (see MemberSummaryListRepository) but only "DOB"
  // and "Updated" are offered - both are plain, cheap columns so it's
  // safe/fast to sort on; other columns keep the fixed default order
  // (companyName, surname) since sorting on them would be expensive or (for
  // Name) requires decrypting every matching row
  readonly memberColumns = [
    {name: "company", label: "Employer", field: (row: any) => row.company.name},
    {name: "number", label: "Member #", field: (row: any) => row.number.toUpperCase()},
    {
      name: "accountStatus",
      label: "Status",
      field: (row: any) => optionLabel(ACCOUNT_STATUS_OPTIONS, row.accountStatus),
    },
    {name: "username", label: "Username", field: "username"},
    {name: "lastLogin", label: "Login", field: "lastLogin", sortable: true, format},
    {name: "name", label: "Name", field: "name"},
    {name: "dob", label: "DOB", field: "dob", sortable: true, format},
    {name: "updated", label: "Updated", field: "updated", sortable: true, format},
  ]

  platformId: string | null = null
  companyId: string | null = null
  number: string | null = null
  name: string | null = null
  members: Array<Member> = new Array<Member>()
  readError = false
  readMessage: string | null = null
  dataInProgress = false
  isNotEmployer = false

  // server-side pagination (and, for the "Updated" column, sorting) state
  // for the q-table (GET /api/members/summary now returns only the
  // requested page - see MemberSummaryPageDto - since computing the
  // per-row summary columns for every matching member was the source of
  // the 10-20s searches for large employers)
  pagination = {
    page: 1,
    rowsPerPage: 20,
    rowsNumber: 0,
    sortBy: <string | null>null,
    descending: false,
  }

  async created() {
    this.isNotEmployer = !(await this.authenticationService.isEmployer())
    this.$watch(
      () => this.planService.engagedOnly,
      async () => {
        if (!this.isSearchDisabled) await this.load()
      }
    )
  }

  private async load() {
    this.pagination.page = 1
    await this.getMembers()
  }

  private async onRequest(requestProperty: {
    pagination: {page: number; rowsPerPage: number; sortBy: string | null; descending: boolean}
  }) {
    const {page, rowsPerPage, sortBy, descending} = requestProperty.pagination
    const sortChanged =
      sortBy !== this.pagination.sortBy || descending !== this.pagination.descending
    this.pagination.page = sortChanged ? 1 : page
    this.pagination.rowsPerPage = rowsPerPage
    this.pagination.sortBy = sortBy
    this.pagination.descending = descending
    await this.getMembers()
  }

  get error(): boolean {
    return this.readError
  }

  get message(): string | null {
    return this.readMessage
  }

  private async getMembers() {
    this.dataInProgress = true
    await Axios.get(`/api/members/summary`, this.filters())
      .then((response: AxiosResponse) => {
        this.readMessage = null
        this.members = response.data.content
        this.pagination.rowsNumber = response.data.totalElements
      })
      .catch((reason: AxiosError<any>) => {
        this.readError = true
        this.readMessage = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  // maps a sortable q-table column name to the entity property
  // MemberSummaryListRepository whitelists for server-side sorting - see
  // MembersView.vue memberColumns/onRequest
  private static readonly SORT_PROPERTIES: Record<string, string> = {
    dob: "dateOfBirth",
    updated: "dateUpdated",
  }

  private filters(): AxiosRequestConfig {
    const parameters: Record<string, any> = {
      page: this.pagination.page - 1,
      size: this.pagination.rowsPerPage,
    }
    const sortProperty =
      this.pagination.sortBy == null
        ? undefined
        : MembersView.SORT_PROPERTIES[this.pagination.sortBy]
    if (sortProperty != null) {
      parameters.sort = `${sortProperty},${this.pagination.descending ? "desc" : "asc"}`
    }

    if (!this.unfiltered) {
      Object.assign(parameters, {
        companyId: this.companyId,
        platformId: this.platformId,
        name: this.name,
        number: this.number,
        engaged: this.planService.engagedOnly,
        ...this.advancedFilterParams(),
      })
    }

    return {params: parameters}
  }

  // simple scalar filters that map 1:1 onto a query param of the same name -
  // see advancedFilterParams
  private static readonly ADVANCED_FILTER_KEYS: ReadonlyArray<keyof MembersView> = [
    "accountStatus",
    "ageMin",
    "ageMax",
    "classification",
    "contributionStatus",
    "beneficiaryNomination",
    "superBalanceMin",
    "superBalanceMax",
    "grossSalaryMin",
    "grossSalaryMax",
    "voluntaryContributionsMin",
    "voluntaryContributionsMax",
    "ttrBalanceMin",
    "ttrBalanceMax",
    "deathCoverMin",
    "deathCoverMax",
    "tpdCoverMin",
    "tpdCoverMax",
    "salaryContinuationCoverMin",
    "salaryContinuationCoverMax",
  ]

  private advancedFilterParams(): Record<string, any> {
    const parameters: Record<string, any> = {}
    if (!this.showFilters) return parameters
    MembersView.ADVANCED_FILTER_KEYS.forEach(key => {
      const value = this[key]
      if (value != null) parameters[key] = value
    })

    if (this.showGroup2) {
      DATE_DROPDOWN_GROUPS.forEach(g => this.applyRange(parameters, this.dropdownSelections[g.id]))
    }

    return parameters
  }

  private applyRange(parameters: Record<string, any>, key: string) {
    const range = this.ranges[key]
    parameters[`${key}From`] = this.toIso(range.from)
    parameters[`${key}To`] = this.toIso(range.to)
  }

  get unfiltered(): boolean {
    return [this.companyId, this.number, this.name, this.planService.engagedOnly].every(_.isEmpty)
  }

  get yearOrLessBounded(): boolean {
    const today = new Date()
    return (
      this.showFilters &&
      DATE_DROPDOWN_GROUPS.some(g => {
        const key = this.dropdownSelections[g.id]
        const range = this.ranges[key]
        if (_.isEmpty(range.from)) return false
        const fromDate = date.extractDate(range.from, DATE_FORMAT)
        const toDate = _.isEmpty(range.to) ? today : date.extractDate(range.to, DATE_FORMAT)
        const diffInDays = date.getDateDiff(toDate, fromDate, "days")
        return diffInDays <= 365
      })
    )
  }

  get isEmployerSearchDisabled(): boolean {
    return (
      _.isEmpty(this.number) &&
      (_.isNull(this.name) || this.name.length < 3) &&
      (!this.isLimitedPlatformFilter || (_.isNull(this.platformId) && !this.yearOrLessBounded))
    )
  }

  get isSearchDisabled(): boolean {
    return this.unfiltered && (this.isNotEmployer || this.isEmployerSearchDisabled)
  }

  get showPlatformFilter(): boolean {
    return this.isNotEmployer || this.isLimitedPlatformFilter
  }

  get isLimitedPlatformFilter(): boolean {
    return (
      !this.isNotEmployer && this.planService.current?.platform.id === UNTAXED_BALANCE_PLATFORM_ID
    )
  }

  get limitedPlatformIds(): string | undefined {
    return this.isLimitedPlatformFilter ? LIMITED_PLATFORM_IDS.join(",") : undefined
  }

  get employerFilterPlatformId(): string | null {
    return this.platformId === TAXED_BALANCE_PLATFORM_ID
      ? UNTAXED_BALANCE_PLATFORM_ID
      : this.platformId
  }

  private platformSelected(platform: IdAndName | null) {
    const previousPlatformId = this.platformId
    const newPlatformId = platform?.id ?? null
    const isLimitedSwap =
      previousPlatformId === newPlatformId ||
      (previousPlatformId != null &&
        newPlatformId != null &&
        LIMITED_PLATFORM_IDS.includes(previousPlatformId) &&
        LIMITED_PLATFORM_IDS.includes(newPlatformId))
    this.platformId = newPlatformId
    if (newPlatformId != null && !isLimitedSwap) {
      this.companyId = null
    }
  }

  private companySelected(company: IdAndName | null) {
    this.companyId = company == null ? null : company.id
  }

  $refs: {
    memberDetailDialog: MemberDetailDialog
    memberDocumentsDialog: MemberDocumentsDialog
  }

  private showMemberDetails(memberId: string) {
    this.$refs.memberDetailDialog.open(memberId)
  }

  private showDocuments(docs: Array<Document>) {
    this.$refs.memberDocumentsDialog.open(docs)
  }

  private profile(f: Flags): string {
    return (
      `${f.enabled ? "Enabled" : "Disabled"}` +
      `${f.activated ? ", Active" : f.enabled ? ", Inactive" : ""}` +
      `${f.locked ? ", Locked" : ""}${f.expired ? ", Expired" : ""}`
    )
  }

  private attributes(f: Flags): string {
    const attributes = new Array<string>()
    if (f.sample) attributes.push("Sample")
    if (f.outdated) attributes.push("Outdated")
    if (f.superscore) attributes.push("SuperScore")
    if (f.projections) attributes.push("Projections")
    if (f.salary) attributes.push("Salary")
    return attributes.join(", ")
  }

  private shadowMemberUrl(member: Member): string {
    return `/api/authentication/shadowLogin/${member.id}?username=${member.username}`
  }
}
